/**
 * Inline SVG icons for the game's chrome.
 *
 * These were emoji until a real iOS device showed why that does not work: a
 * phone renders ✋ and ⚙ as full-colour Apple artwork, which sat beside the
 * monochrome ⊕ and ⊖ glyphs looking like two different applications. Emoji
 * presentation is the platform's choice, not the page's, and `font-variant-
 * emoji` is not supported widely enough to rely on.
 *
 * SVG inherits `currentColor`, so one icon works in both themes and on the
 * accent-filled pressed state, and there are no image requests.
 */

type IconName =
  | 'select' | 'pan' | 'zoomIn' | 'zoomOut' | 'centre' | 'clear'
  | 'players' | 'help' | 'settings' | 'search' | 'shuffle' | 'stuck'
  | 'back' | 'close' | 'play' | 'point' | 'lasso'
  | 'undo' | 'redo' | 'across' | 'down';

const PATHS: Record<IconName, { d: string[]; fill?: string[] }> = {
  // Marquee selection: a dashed rectangle.
  select: { d: ['M4 8V6a2 2 0 0 1 2-2h2', 'M16 4h2a2 2 0 0 1 2 2v2',
                'M20 16v2a2 2 0 0 1-2 2h-2', 'M8 20H6a2 2 0 0 1-2-2v-2'] },
  pan: { d: ['M9 11V5.5a1.5 1.5 0 0 1 3 0V11',
             'M12 11V4.5a1.5 1.5 0 0 1 3 0V11',
             'M15 11V6.5a1.5 1.5 0 0 1 3 0V14a7 7 0 0 1-7 7h-1a7 7 0 0 1-7-7v-2a1.5 1.5 0 0 1 3 0'] },
  // Magnifier with a plus / minus inside — the conventional pair, and legible
  // at 18px where a bare +/- would read as a stray mark.
  zoomIn: { d: ['M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14', 'M20 20l-4.4-4.4',
                'M11 8.2v5.6', 'M8.2 11h5.6'] },
  zoomOut: { d: ['M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14', 'M20 20l-4.4-4.4',
                 'M8.2 11h5.6'] },
  // Centre on content: a crosshair over a ring.
  centre: { d: ['M12 16a4 4 0 1 0 0-8 4 4 0 0 0 0 8',
                'M12 2v3', 'M12 19v3', 'M2 12h3', 'M19 12h3'] },
  clear: { d: ['M3 12a9 9 0 1 0 3-6.7', 'M3 4v5h5'] },
  players: { d: ['M16 20v-1.5a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4V20',
                 'M9 10.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7',
                 'M22 20v-1.5a4 4 0 0 0-3-3.87', 'M16 3.6a4 4 0 0 1 0 7.75'] },
  help: { d: ['M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18',
              'M9.2 9.3a3 3 0 0 1 5.8 1c0 2-3 3-3 3', 'M12 17.5v.01'] },
  settings: { d: ['M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7',
                  'M19.4 14.5a1.7 1.7 0 0 0 .34 1.87l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.7 1.7 0 0 0-2.88 1.2V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-2.94-1.16l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.7 1.7 0 0 0 3.5 14.5H3a2 2 0 1 1 0-4h.1A1.7 1.7 0 0 0 4.26 7.5l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.7 1.7 0 0 0 1.87.34H9a1.7 1.7 0 0 0 1-1.55V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 2.94 1.16l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.7 1.7 0 0 0-.34 1.87V9a1.7 1.7 0 0 0 1.55 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1.5'] },
  search: { d: ['M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14', 'M20 20l-4-4'] },
  shuffle: { d: ['M16 3h5v5', 'M4 20 21 3', 'M21 16v5h-5', 'M15 15l6 6', 'M4 4l5 5'] },
  back: { d: ['M15 19l-7-7 7-7'] },
  // A loose freehand loop with its tail, the way a lasso is drawn.
  lasso: { d: ['M7 16.5C3.6 14.8 2.8 11 5 8.2 7.6 5 13.4 4.2 17.4 6.2c3.6 1.8 3.8 5.6.8 7.8-2.6 1.9-6.6 2.3-9.6 1.5',
               'M8.6 15.5c-1.2 1.4-1.4 3-.4 4.5'] },
  close: { d: ['M6 6l12 12', 'M18 6 6 18'] },
  // Curved arrows turning back and on.
  undo: { d: ['M9 14 4 9l5-5', 'M4 9h10.5a5.5 5.5 0 0 1 0 11H11'] },
  redo: { d: ['M15 14l5-5-5-5', 'M20 9H9.5a5.5 5.5 0 0 0 0 11H13'] },
  // Which way tapped letters go: three squares in a row or a column, with
  // the arrow showing the direction of travel.
  across: { d: ['M3 9h5v6H3z', 'M10 9h5v6h-5z', 'M17 12h4', 'M19 10l2 2-2 2'] },
  down: { d: ['M9 3h6v5H9z', 'M9 10h6v5H9z', 'M12 17v4', 'M10 19l2 2 2-2'] },
  // A filled-looking triangle drawn as a closed stroke, so it matches the
  // weight of everything else in the set.
  play: { d: ['M8 5.5v13l11-6.5z'] },
  // A pointing hand, for the walkthrough's "look at this".
  point: { d: ['M9 11.5V5a1.5 1.5 0 0 1 3 0v6.5',
               'M12 11.5V8.5a1.5 1.5 0 0 1 3 0v3',
               'M15 11.5v-1a1.5 1.5 0 0 1 3 0V16a6 6 0 0 1-6 6h-1a6 6 0 0 1-6-6v-3a1.5 1.5 0 0 1 3 0v1.5'] },
  // A raised hand: "I'm stuck" is a hand up, not a white flag.
  stuck: { d: ['M12 21a7 7 0 0 0 7-7V9a1.5 1.5 0 0 0-3 0',
               'M16 9V6.5a1.5 1.5 0 0 0-3 0V9', 'M13 9V5.5a1.5 1.5 0 0 0-3 0V9',
               'M10 9v-1a1.5 1.5 0 0 0-3 0v7', 'M7 15l-1.6-2.3a1.5 1.5 0 0 0-2.5 1.6L5 21'] },
};

export function icon(name: IconName, size = 18): SVGElement {
  const ns = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(ns, 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('width', String(size));
  svg.setAttribute('height', String(size));
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '1.9');
  svg.setAttribute('stroke-linecap', 'round');
  svg.setAttribute('stroke-linejoin', 'round');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');
  for (const d of PATHS[name].d) {
    const path = document.createElementNS(ns, 'path');
    path.setAttribute('d', d);
    svg.append(path);
  }
  return svg;
}

export type { IconName };
